import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import client from "../lib/api";

/**
 * Forgot password. Asks for an email and always shows the same calm,
 * generic confirmation so no one can tell which emails are registered.
 * In demo mode the backend hands back a reset link so the flow is
 * testable without live email.
 */
export default function ForgotPasswordScreen() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [devToken, setDevToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post("/auth/forgot-password", { email });
      setDone(true);
      if (data?.dev_reset_token) setDevToken(data.dev_reset_token);
    } catch (_e) {
      // Same calm outcome even on a transient error, no enumeration.
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-auth-shell" data-testid="forgot-screen">
      <div className="k-auth-grid" aria-hidden="true" />
      <div className="k-auth-grain" aria-hidden="true" />
      <div className="relative max-w-md mx-auto px-5 py-16 z-10">
        <button onClick={() => nav("/")} className="k-quiet !text-xs inline-flex items-center gap-1 mb-6 hover:underline" data-testid="forgot-back">
          <ArrowLeft size={13} /> Back to sign in
        </button>
        <div className="k-auth-card">
          <div className="p-6 sm:p-7">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: "hsl(var(--klareet-teal) / 0.12)", color: "hsl(var(--klareet-teal))" }}>
              <Mail size={20} />
            </div>
            <h1 className="klareet-serif mb-1" style={{ fontSize: "1.5rem", color: "hsl(var(--klareet-ink))" }} data-testid="forgot-title">
              Reset your password
            </h1>

            {done ? (
              <div data-testid="forgot-done">
                <p className="k-quiet mt-2">
                  If that email is registered, a reset link is on its way. Check your inbox, and your spam folder just in case.
                </p>
                {devToken && (
                  <div className="mt-4 p-3 rounded-lg text-xs" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px dashed hsl(var(--klareet-line))" }} data-testid="forgot-dev-hint">
                    <div className="k-label mb-1">dev shortcut (email is not live yet)</div>
                    <button onClick={() => nav(`/reset-password?token=${devToken}`)} className="k-btn k-btn-primary !py-1.5 !px-3 !text-xs" data-testid="forgot-dev-link">
                      Open the reset link
                    </button>
                  </div>
                )}
                <button onClick={() => nav("/")} className="k-btn k-btn-outline w-full mt-5" data-testid="forgot-return">
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-3 space-y-4">
                <p className="k-quiet">Tell us the email on your account and we will send a link to set a new password.</p>
                <div>
                  <label className="k-label" htmlFor="forgot-email">Email</label>
                  <input
                    id="forgot-email"
                    type="email"
                    required
                    className="k-input mt-1"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@work.example"
                    autoComplete="email"
                    data-testid="forgot-email"
                  />
                </div>
                <button type="submit" disabled={busy} className="k-btn k-btn-accent w-full" data-testid="forgot-submit">
                  {busy ? "Sending." : "Send reset link"}
                </button>
                <p className="k-quiet !text-xs inline-flex items-center gap-1.5">
                  <ShieldCheck size={13} /> the link works once and expires soon
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
